import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import ButtonItem from '@atlaskit/menu/button-item';
import Section from '@atlaskit/menu/section';
import { Anchor, Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { useAdminHubConnectorLink } from '../hooks/useAdminHubConnectorLink.ts';

const messages = defineMessages({
	triggerLabel: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.dismiss.triggerLabel',
		defaultMessage: 'More options',
		description: 'Accessibility label for the dismiss menu trigger button',
	},
	notInterested: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.dismiss.notInterested',
		defaultMessage: 'Not interested',
		description: 'Menu item to dismiss the connector promo',
	},
	infoText: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.dismiss.infoText',
		defaultMessage: 'This is a quick link. Connect to more apps from {administrationLink}.',
		description: 'Info text in the dismiss menu explaining how to connect more apps',
	},
	administrationLinkText: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.dismiss.administrationLinkText',
		defaultMessage: 'Administration',
		description: 'Link text for the administration page',
	},
});

export interface ConnectorDismissMenuProps {
	onDismiss: () => void;
	testId?: string;
}

const styles = cssMap({
	popupContainer: {
		width: '220px',
	},
	infoText: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.150'),
		paddingInline: token('space.200'),
	},
});

export const ConnectorDismissMenu = ({
	onDismiss,
	testId,
}: ConnectorDismissMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const adminHubLink = useAdminHubConnectorLink();
	const { Popup } = useChoreographerAssets();
	const [isOpen, setIsOpen] = useState(false);
	const { createEvent } = useConfluenceAnalyticsEvents();

	const moreIconComponent = useCallback(
		() => (
			<ShowMoreHorizontalIcon
				color={isOpen ? token('color.icon.selected') : token('color.icon')}
				label=""
				size="small"
			/>
		),
		[isOpen],
	);

	const handleDismiss = useCallback(() => {
		createEvent({
			type: 'sendUIEvent',
			data: {
				source: 'ConnectorDismissMenu',
				action: 'clicked',
				actionSubject: 'button',
				attributes: {
					container: 'ConnectorPromoNavSearch',
					testId: testId ? `${testId}--not-interested` : 'connector-dismiss-menu--not-interested',
				},
			},
		}).fire();
		setIsOpen(false);
		onDismiss();
	}, [onDismiss, createEvent, testId]);

	const handleTriggerClick = useCallback(() => {
		if (!isOpen) {
			createEvent({
				type: 'sendUIEvent',
				data: {
					source: 'ConnectorDismissMenu',
					action: 'clicked',
					actionSubject: 'button',
					attributes: {
						container: 'ConnectorPromoNavSearch',
						testId: testId ? `${testId}--trigger` : 'connector-dismiss-menu--trigger',
					},
				},
			}).fire();
		}
		setIsOpen((prev) => !prev);
	}, [isOpen, createEvent, testId]);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="right-end"
			content={() => (
				<div css={styles.popupContainer} data-testid={testId || 'connector-dismiss-menu'}>
					<Section>
						<ButtonItem
							onClick={handleDismiss}
							testId={
								testId ? `${testId}--not-interested` : 'connector-dismiss-menu--not-interested'
							}
						>
							{formatMessage(messages.notInterested)}
						</ButtonItem>
					</Section>
					<Section hasSeparator>
						<Box xcss={styles.infoText}>
							<Text size="small" color="color.text.subtlest">
								{formatMessage(messages.infoText, {
									administrationLink: (
										<Anchor href={adminHubLink ?? '/admin'}>
											{formatMessage(messages.administrationLinkText)}
										</Anchor>
									),
								})}
							</Text>
						</Box>
					</Section>
				</div>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					icon={moreIconComponent}
					label={formatMessage(messages.triggerLabel)}
					appearance="subtle"
					spacing="compact"
					isSelected={isOpen}
					onClick={handleTriggerClick}
					testId={testId ? `${testId}--trigger` : 'connector-dismiss-menu--trigger'}
				/>
			)}
		/>
	);
};
